import {
  Avatar,
  AvatarFallback,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useSidebar,
} from '@sho-bot/ui';
import {
  IconActivityHeartbeat,
  IconAdjustments,
  IconArrowLeft,
  IconChevronDown,
  IconDatabase,
  IconHome,
  IconSearch,
  IconServer,
  IconSettings,
  IconSlash,
  type IconProps,
} from '@tabler/icons-react';
import { useState, type ComponentType, type ReactNode } from 'react';
import type { GuildSummary } from '../../shared/api.js';
import { ROUTE_TITLES, type RoutePath } from '../navigation.js';

/**
 * The application shell.
 *
 * `SidebarInset` renders a `<main>`, so the page has exactly one main landmark
 * and the skip link has somewhere to go. The sidebar itself is a `<nav>` with
 * real buttons, so it is operable by keyboard without anything extra.
 *
 * The header menu selects a server and exposes the same compact account actions
 * as Adachi. The persistent footer entry remains the primary settings route.
 * Page titles live in the inset chrome header, not in the scrolling body.
 */

interface Destination {
  path: RoutePath;
  icon: ComponentType<IconProps>;
}

const DESTINATIONS: readonly Destination[] = [
  { path: '/', icon: IconHome },
  { path: '/memory', icon: IconDatabase },
  { path: '/query-lab', icon: IconSearch },
  { path: '/operations', icon: IconActivityHeartbeat },
];

const SETTINGS_DESTINATIONS: readonly Destination[] = [
  { path: '/server-settings', icon: IconServer },
  { path: '/settings', icon: IconAdjustments },
];

export const MAIN_CONTENT_ID = 'main-content';

function serverInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || 'S';
}

export function AppShell({
  currentPath,
  onNavigate,
  guilds,
  selectedGuildId,
  onSelectGuild,
  onSignOut,
  children,
}: {
  currentPath: RoutePath;
  onNavigate: (path: RoutePath) => void;
  guilds: GuildSummary[];
  selectedGuildId: string | null;
  onSelectGuild: (guildId: string | null) => void;
  onSignOut: () => void;
  children: ReactNode;
}): ReactNode {
  const selectedGuild = guilds.find((guild) => guild.guildId === selectedGuildId) ?? null;
  const isSettings = currentPath === '/server-settings' || currentPath === '/settings';
  const [serverMenuOpen, setServerMenuOpen] = useState(false);
  const settingsEntry = selectedGuildId === null ? '/settings' : '/server-settings';

  return (
    <SidebarProvider className="h-svh overflow-hidden">
      <a className="skip-link" href={`#${MAIN_CONTENT_ID}`}>
        Skip to main content
      </a>

      <Sidebar collapsible="icon">
        <SidebarHeader className="p-2">
          {isSettings ? (
            <CollapsedChromeTooltip label="Back to app">
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="inline"
                    size="provider"
                    layout="full"
                    data-sidebar="back"
                    aria-label="Back to app"
                    className="justify-start"
                    onClick={() => onNavigate('/')}
                  />
                }
              >
                <IconArrowLeft aria-hidden="true" className="size-4" stroke={1.5} />
                <span data-sidebar="back-label">Back to app</span>
              </TooltipTrigger>
            </CollapsedChromeTooltip>
          ) : (
            <div className="flex w-full items-center gap-1">
              <DropdownMenu open={serverMenuOpen} onOpenChange={setServerMenuOpen}>
                <CollapsedChromeTooltip label={selectedGuild?.name ?? 'Choose a server'}>
                  <TooltipTrigger
                    render={
                      <DropdownMenuTrigger
                        render={
                          <Button
                            data-sidebar="header-account"
                            variant="inline"
                            size="provider"
                            layout="fit"
                            className="min-w-0 shrink justify-start"
                            aria-label="Choose server"
                          />
                        }
                      />
                    }
                  >
                    <div className="flex min-w-0 items-center gap-1.5">
                      <Avatar size="xs" radius="md">
                        <AvatarFallback>S</AvatarFallback>
                      </Avatar>
                      <span
                        data-sidebar="header-account-label"
                        className="min-w-0 flex-1 truncate text-sm font-medium"
                      >
                        {selectedGuild?.name ?? 'Choose a server'}
                      </span>
                    </div>
                    <IconChevronDown
                      data-sidebar="header-account-chevron"
                      aria-hidden="true"
                      className="size-4 text-muted-foreground"
                      stroke={1.5}
                    />
                  </TooltipTrigger>
                </CollapsedChromeTooltip>
                <DropdownMenuContent side="bottom" align="start" className="w-64">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Servers</DropdownMenuLabel>
                    {guilds.length === 0 ? (
                      <DropdownMenuItem disabled>No servers available</DropdownMenuItem>
                    ) : (
                      <DropdownMenuRadioGroup
                        value={selectedGuildId ?? ''}
                        onValueChange={(guildId) => {
                          if (typeof guildId === 'string') {
                            onSelectGuild(guildId);
                            setServerMenuOpen(false);
                          }
                        }}
                      >
                        {guilds.map((guild) => (
                          <DropdownMenuRadioItem key={guild.guildId} value={guild.guildId}>
                            <Avatar size="xs" radius="md">
                              <AvatarFallback>{serverInitial(guild.name)}</AvatarFallback>
                            </Avatar>
                            <span className="min-w-0 flex-1 truncate">{guild.name}</span>
                            {!guild.inBotCache ? (
                              <span className="text-xs text-muted-foreground">Offline</span>
                            ) : null}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    )}
                  </DropdownMenuGroup>
                  <div className="flex items-center gap-1 p-1">
                    <Button
                      variant="outline"
                      size="sm"
                      layout="fit"
                      onClick={() => {
                        setServerMenuOpen(false);
                        onNavigate(settingsEntry);
                      }}
                    >
                      Settings
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      layout="fit"
                      onClick={() => {
                        setServerMenuOpen(false);
                        onSignOut();
                      }}
                    >
                      Log out
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <div data-sidebar="header-actions" className="ml-auto flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger
                    render={<SidebarTrigger data-sidebar="header-trigger" size="icon-xs" />}
                  />
                  <TooltipContent>Toggle sidebar</TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            {isSettings ? <SidebarGroupLabel>Settings</SidebarGroupLabel> : null}
            <SidebarGroupContent>
              <SidebarMenu>
                {(isSettings ? SETTINGS_DESTINATIONS : DESTINATIONS).map((destination) => {
                  const Icon = destination.icon;
                  const label = ROUTE_TITLES[destination.path];
                  const active = destination.path === currentPath;
                  const disabled =
                    destination.path === '/server-settings' && selectedGuildId === null;
                  return (
                    <SidebarMenuItem key={destination.path}>
                      <SidebarMenuButton
                        isActive={active}
                        aria-current={active ? 'page' : undefined}
                        disabled={disabled}
                        tooltip={label}
                        onClick={() => {
                          onNavigate(destination.path);
                        }}
                      >
                        <Icon aria-hidden="true" stroke={1.5} />
                        <span>{label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        {!isSettings ? (
          <SidebarFooter className="p-2">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton tooltip="Settings" onClick={() => onNavigate(settingsEntry)}>
                  <IconSettings aria-hidden="true" stroke={1.5} />
                  <span>Settings</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        ) : null}
        <SidebarRail toggleOnClick={false} />
      </Sidebar>

      <SidebarInset id={MAIN_CONTENT_ID} tabIndex={-1} className="min-h-0 overflow-hidden">
        <SidebarTrigger
          data-sidebar="mobile-trigger"
          size="icon-xs"
          className="absolute left-4 top-3 z-10 sm:hidden"
        />
        <Tooltip>
          <TooltipTrigger render={<SidebarTrigger data-sidebar="inset-trigger" size="icon-xs" />} />
          <TooltipContent side="right">Toggle sidebar</TooltipContent>
        </Tooltip>
        <PageChrome
          currentPath={currentPath}
          isSettings={isSettings}
          onOpenSettings={() => onNavigate(settingsEntry)}
        />
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <div className="mx-auto min-w-0 w-full max-w-7xl px-4 pb-10 pt-6 lg:px-8">{children}</div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function CollapsedChromeTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactNode {
  const { isMobile, state } = useSidebar();

  return (
    <Tooltip>
      {children}
      <TooltipContent align="center" hidden={state !== 'collapsed' || isMobile} side="right">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function PageChrome({
  currentPath,
  isSettings,
  onOpenSettings,
}: {
  currentPath: RoutePath;
  isSettings: boolean;
  onOpenSettings: () => void;
}): ReactNode {
  const title = ROUTE_TITLES[currentPath];

  return (
    <header className="flex h-12 shrink-0 items-center overflow-hidden border-b border-border-secondary px-2 max-sm:pl-10 group-data-[state=collapsed]/inset:pl-10">
      {isSettings ? (
        <Breadcrumb className="min-w-0 flex-1 px-2">
          <BreadcrumbList className="px-0">
            <BreadcrumbItem>
              <BreadcrumbLink render={<button type="button" onClick={onOpenSettings} />}>
                Settings
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <IconSlash stroke={1.5} />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>
                <h1 className="truncate text-sm font-medium">{title}</h1>
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      ) : (
        <h1 className="min-w-0 flex-1 truncate px-2 text-sm font-medium">{title}</h1>
      )}
    </header>
  );
}
